/**
 * rendering-registry/registry.ts
 *
 * THE MACHINE-READABLE SOURCE OF TRUTH for TeachBud visual representations.
 *
 * All four resource generators (Activity Sheet, Presentation, Assessment,
 * Lesson Plan) read from this file. Adding a new representation means:
 *
 *   1. Add a RegistryEntry here (this file only)
 *   2. Build the React component in src/representations/
 *   3. Add a case in the rendering dispatch of each affected generator
 *   4. Run the regression test suite
 *
 * See docs/adding-a-representation.md for the full 6-step process.
 *
 * ─── VERSIONING ───────────────────────────────────────────────────────────────
 * Bump REGISTRY_VERSION when you change this file:
 *   MAJOR — breaking change to universal rules or blueprint structure
 *   MINOR — new representation added (additive, low risk)
 *   PATCH — parameter tweak, default adjustment, fallback change
 *
 * Resources stamp the version that produced them. Teachers see an
 * "updates available" badge and can regenerate when they choose.
 */

import type {
  RegistryEntry,
  WorkspaceKind,
  WorkspaceSize,
} from './types';

// ── Version ────────────────────────────────────────────────────────────────────

/** Bump when this file changes. Stamped on every saved resource. */
export const REGISTRY_VERSION = '3.3.0';

// ── Workspace height helper ────────────────────────────────────────────────────

/** Constructs a wsHeights record from (small, medium, large, xl) values. */
function h(
  small: number,
  medium: number,
  large: number,
  xl?: number,
): Record<WorkspaceSize, number> {
  return {
    none:   0,
    small,
    medium,
    large,
    xl:     xl ?? Math.round(large * 1.3),
  };
}

// ── Canonical workspace heights ────────────────────────────────────────────────
//
// Exported so generators can look up heights without scanning entries.
// Add a new WorkspaceKind here when adding a new representation.

export const WS_HEIGHTS_BY_KIND: Record<WorkspaceKind, Record<WorkspaceSize, number>> = {
  'none':           h(0,   0,   0,   0),
  'lines':          h(40,  64,  88,  120),
  'computation':    h(80,  112, 152, 208),
  'diagram':        h(128, 192, 256, 336),
  'ten-frame':      h(160, 200, 240, 280),
  'number-bond':    h(140, 180, 220, 260),
  'fraction-strip':  h(100, 140, 180, 220),
  'number-line':     h(80,  100, 120, 140),
  'sequence-chain':  h(140, 160, 200, 240),
  'venn-diagram':    h(200, 240, 280, 320),
  'cer-organizer':   h(260, 300, 360, 420),
  't-chart':         h(180, 220, 260, 300),
  'story-map':       h(180, 210, 250, 290),
  'main-idea-web':   h(200, 240, 290, 330),
  'sound-boxes':     h(140, 160, 180, 200),
  'picture-letter':  h(240, 280, 320, 360),
};

// ── Registry entries ───────────────────────────────────────────────────────────
//
// ORDER MATTERS: the resolver picks the HIGHEST-SCORING match, but when two
// entries tie, the first one in this array wins. Put more specific entries
// before the catch-all.
//
// FALLBACK CHAIN INVARIANT: Visual representations must fall back to other
// visual representations — never to a generic computation or text box.
//
//   Visual chain:  ten-frame → diagram-scaffold → (terminal: lines)
//   Math chain:    number-bond → (terminal: computation)
//   Science chain: flow-diagram → diagram-scaffold → (terminal: lines)
//   Generic draw:  diagram-scaffold → (terminal: lines)
//   Text:          lines-written-response → (terminal: lines)
//   Default:       computation-default → (terminal: computation)

export const REGISTRY: RegistryEntry[] = [

  // ── K–2: Ten Frame (explicit mention) ───────────────────────────────────────
  {
    id: 'ten-frame-k2-explicit',
    specVersion: '3.1.0',
    description: 'Ten-frame diagram for any K–2 prompt that explicitly mentions a ten frame.',
    match: {
      gradeRange: [0, 2],
      subjects: ['Math'],
      promptPatterns: [
        'ten.?frame',
        'fill in the frame',
        'show.*on the frame',
      ],
    },
    representation: {
      type: 'ten-frame',
      imageHintPattern: 'ten.?frame',
      imageHintExamples: [
        'ten-frame with 7 filled',
        'empty ten-frame for student',
        'ten-frame with 14 filled',
      ],
    },
    activitySheet: {
      workspaceKind: 'ten-frame',
      wsHeights: WS_HEIGHTS_BY_KIND['ten-frame'],
    },
    presentation: {
      imageHintExample: 'ten-frame with 7 filled',
    },
    assessment: { allowed: true, workspaceKind: 'ten-frame' },
    // Stays visual: blank drawing space — never degrades to computation
    fallback: { kind: 'entry', id: 'diagram-scaffold' },
    editableProps: ['value', 'studentMode'],
    examples: [
      'Show 7 on a ten frame.',
      'Fill in the ten frame to show 13.',
    ],
  },

  // ── K–1: Ten Frame (implicit counting language) ──────────────────────────────
  {
    id: 'ten-frame-k1-implicit',
    specVersion: '3.1.0',
    description: 'Ten-frame workspace for K–1 counting/quantity prompts that use standard counting language ("how many", "count the", etc.) without explicitly naming the ten frame.',
    match: {
      gradeRange: [0, 1],
      subjects: ['Math'],
      promptPatterns: [
        'how many',
        'count the',
        'show the number',
        'fill in.*circle',
        'color.*circle',
        'counters',
      ],
    },
    representation: {
      type: 'ten-frame',
      imageHintPattern: 'ten.?frame',
      imageHintExamples: [
        'ten-frame with 5 filled',
        'empty ten-frame for student',
      ],
    },
    activitySheet: {
      workspaceKind: 'ten-frame',
      wsHeights: WS_HEIGHTS_BY_KIND['ten-frame'],
    },
    presentation: {
      imageHintExample: 'ten-frame with 5 filled',
    },
    assessment: { allowed: true, workspaceKind: 'ten-frame' },
    // Chain: implicit K-1 → explicit K-2 → diagram-scaffold
    fallback: { kind: 'entry', id: 'ten-frame-k2-explicit' },
    editableProps: ['value', 'studentMode'],
    examples: [
      'How many apples are there?',
      'Count the dots and write the number.',
    ],
  },

  // ── K–2: Number Bond (part-part-whole) ───────────────────────────────────────
  {
    id: 'number-bond-k2',
    specVersion: '3.1.0',
    description: 'Number bond (part-part-whole) diagram for K–2 addition and subtraction.',
    match: {
      gradeRange: [0, 2],
      subjects: ['Math'],
      promptPatterns: [
        'number.?bond',
        'part.?part.?whole',
        'missing part',
        'parts that make',
        'complete the bond',
      ],
    },
    representation: {
      type: 'number-bond',
      imageHintPattern: 'number.?bond|part.?part.?whole|part.*whole',
      imageHintExamples: [
        'number bond: 8 = 5 + 3',
        'number bond: 10 = 6 + ?',
        'number bond: 7 = ? + 4',
      ],
    },
    activitySheet: {
      workspaceKind: 'number-bond',
      wsHeights: WS_HEIGHTS_BY_KIND['number-bond'],
    },
    presentation: {
      imageHintExample: 'number bond: 8 = 5 + 3',
    },
    assessment: { allowed: true, workspaceKind: 'number-bond' },
    // Math workspace is an appropriate fallback for a math diagram
    fallback: { kind: 'terminal', workspaceKind: 'computation' },
    editableProps: ['whole', 'part1', 'part2', 'studentMode'],
    examples: [
      'Complete the number bond: 8 = ___ + 3',
      'Find the missing part: 10 = 6 + ___',
    ],
  },

  // ── Science / Social Studies: Flow Diagram ───────────────────────────────────
  {
    id: 'flow-diagram-science',
    specVersion: '3.1.0',
    description: 'Node-and-arrow flow diagram for food webs, matter flow, energy flow, and cause-effect chains.',
    match: {
      subjects: ['Science', 'Social Studies'],
      promptPatterns: [
        'food web',
        'food chain',
        'matter.*flow',
        'energy.*flow',
        'cause.*effect.*chain',
        'flow.*diagram',
      ],
    },
    representation: {
      type: 'flow-diagram',
      imageHintPattern: 'food web|food chain|matter.*flow|energy.*flow|cause.*effect chain|flow diagram',
      imageHintExamples: [
        'food web: grass → grasshopper → frog → snake → hawk',
        'matter flow: solid → liquid → gas',
        'cause-effect chain: drought → less food → population decline',
      ],
    },
    activitySheet: {
      // Blank drawing space — teacher uses it to have students draw the flow
      workspaceKind: 'diagram',
      wsHeights: WS_HEIGHTS_BY_KIND['diagram'],
    },
    presentation: {
      imageHintExample: 'food web: grass → grasshopper → frog → hawk',
    },
    assessment: { allowed: true, workspaceKind: 'diagram' },
    // Falls back to a blank visual drawing space — never to computation
    fallback: { kind: 'entry', id: 'diagram-scaffold' },
    editableProps: [],
    examples: [
      'Draw a food web showing three organisms.',
      'Show how matter flows through the states.',
    ],
  },

  // ── Generic Diagram / Sketch Space ───────────────────────────────────────────
  {
    id: 'diagram-scaffold',
    specVersion: '3.1.0',
    description: 'Blank diagram/sketch space with dot grid. For draw/model/sketch tasks where no more-specific visual component applies.',
    match: {
      promptPatterns: [
        'draw',
        'model',
        'diagram',
        'sketch',
        'shade',
        'rectangle',
        'fraction bar',
        'number line',
        '\\bplot\\b',
        'picture',
        'area model',
        '\\bgrid\\b',
        '\\bgraph\\b',
      ],
    },
    representation: {
      type: 'diagram',
      imageHintPattern: 'diagram|sketch|draw a|number line|area model',
      imageHintExamples: [
        'diagram: number line from 0 to 10',
        'sketch: rectangle divided into 4 equal parts',
      ],
    },
    activitySheet: {
      workspaceKind: 'diagram',
      wsHeights: WS_HEIGHTS_BY_KIND['diagram'],
    },
    presentation: {
      imageHintExample: 'diagram: number line from 0 to 10',
    },
    assessment: { allowed: true, workspaceKind: 'diagram' },
    // Final visual safety net before terminal — lines are always available
    fallback: { kind: 'terminal', workspaceKind: 'lines' },
    editableProps: [],
    examples: [
      'Draw a rectangle and divide it into equal parts.',
      'Plot the point on the number line.',
    ],
  },

  // ── Written Response / Explanation ───────────────────────────────────────────
  {
    id: 'lines-written-response',
    specVersion: '3.1.0',
    description: 'Ruled answer lines for written responses, explanations, sentence-level ELA, and justify/analyze tasks.',
    match: {
      promptPatterns: [
        'explain',
        'describe',
        'write a',
        'write your',
        'sentence',
        'paragraph',
        'why',
        'how do you know',
        'justify',
        'reason',
        'define',
        'in words',
        'in writing',
        'respond',
        'reflect',
      ],
    },
    representation: {
      type: 'lines',
    },
    activitySheet: {
      workspaceKind: 'lines',
      wsHeights: WS_HEIGHTS_BY_KIND['lines'],
    },
    assessment: { allowed: true, workspaceKind: 'lines' },
    // Terminal — lines always render
    fallback: { kind: 'terminal', workspaceKind: 'lines' },
    editableProps: ['answerLinesCount'],
    examples: [
      'Explain your thinking in a complete sentence.',
      'Why does the character make that choice?',
    ],
  },

  // ── Grades 3–5: Fraction Strip ───────────────────────────────────────────────
  //
  // A blank computation box is INSTRUCTIONALLY WRONG for fraction strip tasks —
  // shading, comparing, and identifying equivalent fractions requires the visual.
  // If FractionStripSVG fails to render, BLOCK EXPORT rather than silently
  // produce an inferior worksheet. This is the first production use of block-export.
  {
    id: 'fraction-strip-k5',
    specVersion: '3.2.0',
    description: 'Fraction strip (tape diagram) for Grades 3–5 fraction concepts. Export is blocked if the component cannot render — a blank box is instructionally insufficient.',
    match: {
      gradeRange: [3, 5],
      subjects: ['Math'],
      promptPatterns: [
        'fraction strip',
        'tape diagram',
        'shade.*fraction',
        'shade.*part',
        'fraction.*bar',
        'compare.*fraction',
        'equivalent fraction',
        'number line.*fraction',
      ],
    },
    representation: {
      type: 'fraction-strip',
      imageHintPattern: 'fraction strip|tape diagram',
      imageHintExamples: [
        'fraction strip: 3/4 shaded',
        'fraction strip: compare 2/3 and 3/4',
        'tape diagram: 2 equal parts, 1 shaded',
      ],
    },
    activitySheet: {
      workspaceKind: 'fraction-strip',
      wsHeights: WS_HEIGHTS_BY_KIND['fraction-strip'],
    },
    presentation: { imageHintExample: 'fraction strip: 3/4 shaded' },
    assessment: { allowed: true, workspaceKind: 'fraction-strip' },
    /**
     * BLOCK-EXPORT fallback — this is an intentional hard stop.
     *
     * Why not fall back to 'diagram'?
     * A blank drawing box teaches nothing about fractions. Teachers would receive
     * a worksheet that looks complete but has no instructional scaffold. That's
     * worse than an honest error that says "this feature isn't ready yet."
     *
     * When FractionStripSVG is built (see docs/adding-a-representation.md),
     * change the fallback to: { kind: 'entry', id: 'diagram-scaffold' }
     */
    // FractionStripSVG now ships with v3.3.0 — fallback changed from block-export to terminal.
    fallback: { kind: 'terminal', workspaceKind: 'fraction-strip' },
    editableProps: ['numerator', 'denominator', 'studentMode'],
    examples: [
      'Shade 3/4 on the fraction strip.',
      'Use fraction strips to compare 2/3 and 3/4.',
      'Draw a tape diagram to show 5/6.',
    ],
  },

  // ── K–5 Math: Number Line ─────────────────────────────────────────────────────
  {
    id: 'number-line-k5',
    specVersion: '3.3.0',
    description: 'Number line for K–5 math: counting, addition/subtraction hops, fractions, integers.',
    match: {
      gradeRange: [0, 8],
      subjects: ['Math'],
      promptPatterns: [
        'number line',
        'hop.*jump',
        'jump.*number',
        'count.*by',
        'plot.*point',
        'mark.*number',
        'locate.*number',
        'open number line',
      ],
    },
    representation: {
      type: 'number-line',
      imageHintPattern: 'number line',
      imageHintExamples: ['number line 0 to 10', 'number line 0 to 20 with hops', 'fraction number line 0 to 1 in fourths'],
    },
    activitySheet: { workspaceKind: 'number-line', wsHeights: WS_HEIGHTS_BY_KIND['number-line'] },
    presentation: { imageHintExample: 'number line 0 to 10' },
    assessment: { allowed: true, workspaceKind: 'number-line' },
    fallback: { kind: 'terminal', workspaceKind: 'number-line' },
    editableProps: ['start', 'end', 'interval', 'fractionDenom', 'studentMode'],
    examples: ['Jump from 3 to 8 on the number line.', 'Plot 3/4 on the fraction number line.'],
  },

  // ── K–5 ELA: Story Map ────────────────────────────────────────────────────────
  {
    id: 'story-map-k5',
    specVersion: '3.3.0',
    description: 'Story map (narrative organizer) for K–5 ELA. 4 boxes for K–1, 5 boxes for 2–5.',
    match: {
      gradeRange: [0, 5],
      subjects: ['ELA'],
      promptPatterns: [
        'story map',
        'characters.*setting',
        'setting.*problem',
        'narrative.*organizer',
        'beginning.*middle.*end',
        'plot.*diagram',
        'characters.*problem',
      ],
    },
    representation: {
      type: 'story-map',
      imageHintPattern: 'story map|narrative organizer|beginning.*middle.*end',
      imageHintExamples: ['story map: 4 boxes', 'story map: 5 boxes with events'],
    },
    activitySheet: { workspaceKind: 'story-map', wsHeights: WS_HEIGHTS_BY_KIND['story-map'] },
    presentation: { imageHintExample: 'story map: 4 boxes' },
    assessment: { allowed: true, workspaceKind: 'story-map' },
    fallback: { kind: 'terminal', workspaceKind: 'story-map' },
    editableProps: ['boxCount', 'boxLabels', 'studentMode'],
    examples: ['Fill in the story map: Characters, Setting, Problem, Solution.'],
  },

  // ── 2–6 ELA: Main Idea Web ────────────────────────────────────────────────────
  {
    id: 'main-idea-web',
    specVersion: '3.3.0',
    description: 'Main idea web (spider map) for 2–6 ELA informational reading.',
    match: {
      gradeRange: [2, 6],
      subjects: ['ELA'],
      promptPatterns: [
        'main idea',
        'main idea web',
        'supporting detail',
        'central idea',
        'key detail',
        'topic.*detail',
        'main.*supporting',
      ],
    },
    representation: {
      type: 'main-idea-web',
      imageHintPattern: 'main idea web|main idea.*detail|central idea',
      imageHintExamples: ['main idea web: 3 details', 'main idea web: central idea with 4 details'],
    },
    activitySheet: { workspaceKind: 'main-idea-web', wsHeights: WS_HEIGHTS_BY_KIND['main-idea-web'] },
    presentation: { imageHintExample: 'main idea web: 3 details' },
    assessment: { allowed: true, workspaceKind: 'main-idea-web' },
    fallback: { kind: 'terminal', workspaceKind: 'main-idea-web' },
    editableProps: ['detailCount', 'studentMode'],
    examples: ['Complete the main idea web with 3 supporting details.'],
  },

  // ── K–1 ELA: Sound Boxes (Elkonin) ───────────────────────────────────────────
  {
    id: 'sound-boxes-k1',
    specVersion: '3.3.0',
    description: 'Elkonin (sound) boxes for K–1 ELA phonemic awareness.',
    match: {
      gradeRange: [0, 1],
      subjects: ['ELA'],
      promptPatterns: [
        'sound box',
        'elkonin',
        'phoneme.*box',
        'segment.*sound',
        'push.*counter',
        'tap.*sound',
        'break.*sound',
        'count.*sound',
      ],
    },
    representation: {
      type: 'sound-boxes',
      imageHintPattern: 'sound boxes|elkonin|phoneme boxes',
      imageHintExamples: ['sound boxes for "cat" (3 sounds)', 'elkonin boxes: 4 phonemes'],
    },
    activitySheet: { workspaceKind: 'sound-boxes', wsHeights: WS_HEIGHTS_BY_KIND['sound-boxes'] },
    presentation: { imageHintExample: 'sound boxes for "cat" (3 sounds)' },
    assessment: { allowed: true, workspaceKind: 'sound-boxes' },
    fallback: { kind: 'terminal', workspaceKind: 'sound-boxes' },
    editableProps: ['phonemeCount', 'word', 'studentMode'],
    examples: ['Use the sound boxes to segment the word "ship".'],
  },

  // ── K–1 ELA: Picture-Letter Card ─────────────────────────────────────────────
  {
    id: 'picture-letter-k1',
    specVersion: '3.3.0',
    description: 'Picture-letter card with tracing guide for K–1 ELA phonics.',
    match: {
      gradeRange: [0, 1],
      subjects: ['ELA'],
      promptPatterns: [
        'picture.*letter',
        'letter card',
        'letter.*for',
        'phonics card',
        'trace.*letter',
        'practice.*letter',
        'alphabet.*card',
      ],
    },
    representation: {
      type: 'picture-letter',
      imageHintPattern: 'picture.*letter|letter card|phonics card',
      imageHintExamples: ['letter card: A for apple', 'picture-letter card: B for ball'],
    },
    activitySheet: { workspaceKind: 'picture-letter', wsHeights: WS_HEIGHTS_BY_KIND['picture-letter'] },
    presentation: { imageHintExample: 'letter card: A for apple' },
    assessment: { allowed: true, workspaceKind: 'picture-letter' },
    fallback: { kind: 'terminal', workspaceKind: 'picture-letter' },
    editableProps: ['letter', 'objectName', 'studentMode'],
    examples: ['Trace the letter A and draw a picture of an apple.'],
  },

  // ── 2–8 Multi-subject: Sequence Chain ────────────────────────────────────────
  {
    id: 'sequence-chain',
    specVersion: '3.3.0',
    description: 'Sequence chain (boxes + arrows) for 2–8 ELA, Science, and Social Studies.',
    match: {
      gradeRange: [2, 8],
      promptPatterns: [
        'sequence',
        'order.*steps',
        'steps in order',
        'what.*first.*then',
        'put.*order',
        'order.*events',
        'chain.*events',
        'what happens.*next',
        'steps.*process',
      ],
    },
    representation: {
      type: 'sequence-chain',
      imageHintPattern: 'sequence chain|steps in order|order.*events',
      imageHintExamples: ['sequence chain: 4 steps', 'sequence chain: 5 events in order'],
    },
    activitySheet: { workspaceKind: 'sequence-chain', wsHeights: WS_HEIGHTS_BY_KIND['sequence-chain'] },
    presentation: { imageHintExample: 'sequence chain: 4 steps' },
    assessment: { allowed: true, workspaceKind: 'sequence-chain' },
    fallback: { kind: 'terminal', workspaceKind: 'sequence-chain' },
    editableProps: ['steps', 'stepLabels', 'studentMode'],
    examples: ['Put the steps in order: First, Next, Then, Finally.'],
  },

  // ── K–8 Multi-subject: Venn Diagram ──────────────────────────────────────────
  {
    id: 'venn-diagram',
    specVersion: '3.3.0',
    description: 'Two-circle Venn diagram for K–8 compare/contrast across all subjects.',
    match: {
      gradeRange: [0, 8],
      promptPatterns: [
        'venn diagram',
        'compare.*contrast',
        'similarities.*differences',
        'how.*alike.*different',
        'same.*different',
        'compare.*two',
        'both.*different',
      ],
    },
    representation: {
      type: 'venn-diagram',
      imageHintPattern: 'venn diagram|compare.*contrast',
      imageHintExamples: ['venn diagram: frogs and toads', 'venn diagram: plant cells and animal cells'],
    },
    activitySheet: { workspaceKind: 'venn-diagram', wsHeights: WS_HEIGHTS_BY_KIND['venn-diagram'] },
    presentation: { imageHintExample: 'venn diagram: Topic A and Topic B' },
    assessment: { allowed: true, workspaceKind: 'venn-diagram' },
    fallback: { kind: 'terminal', workspaceKind: 'venn-diagram' },
    editableProps: ['leftLabel', 'rightLabel', 'studentMode'],
    examples: ['Use the Venn diagram to compare frogs and toads.'],
  },

  // ── K–8 Multi-subject: T-Chart ────────────────────────────────────────────────
  {
    id: 't-chart',
    specVersion: '3.3.0',
    description: 'T-chart (two-column organizer) for K–8 compare/contrast, pros/cons, cause/effect.',
    match: {
      gradeRange: [0, 8],
      promptPatterns: [
        't.?chart',
        'two.?column',
        'pros.*cons',
        'advantages.*disadvantages',
        'plant.*animal',
        'living.*nonliving',
        'push.*pull',
        'cause.*effect',
      ],
    },
    representation: {
      type: 't-chart',
      imageHintPattern: 't-chart|t chart|two column organizer|pros.*cons',
      imageHintExamples: ['t-chart: plants vs animals', 't-chart: pros and cons', 't-chart: cause and effect'],
    },
    activitySheet: { workspaceKind: 't-chart', wsHeights: WS_HEIGHTS_BY_KIND['t-chart'] },
    presentation: { imageHintExample: 't-chart: two columns' },
    assessment: { allowed: true, workspaceKind: 't-chart' },
    fallback: { kind: 'terminal', workspaceKind: 't-chart' },
    editableProps: ['leftLabel', 'rightLabel', 'rowCount', 'studentMode'],
    examples: ['Sort the words into the T-chart: living things and nonliving things.'],
  },

  // ── 4–8 ELA: CER Organizer ────────────────────────────────────────────────────
  {
    id: 'cer-organizer',
    specVersion: '3.3.0',
    description: 'CER (Claim-Evidence-Reasoning) organizer for 4–8 ELA argument and opinion writing.',
    match: {
      gradeRange: [4, 8],
      subjects: ['ELA'],
      promptPatterns: [
        '\\bCER\\b',
        'claim.*evidence',
        'claim.*reasoning',
        'argument.*evidence',
        'justify.*claim',
        'support.*claim',
        'opinion.*reason',
        'i claim',
        'evidence.*reasoning',
      ],
    },
    representation: {
      type: 'cer-organizer',
      imageHintPattern: 'CER organizer|claim.*evidence.*reasoning',
      imageHintExamples: ['CER organizer: claim, evidence, reasoning'],
    },
    activitySheet: { workspaceKind: 'cer-organizer', wsHeights: WS_HEIGHTS_BY_KIND['cer-organizer'] },
    presentation: { imageHintExample: 'CER organizer: claim, evidence, reasoning' },
    assessment: { allowed: true, workspaceKind: 'cer-organizer' },
    fallback: { kind: 'terminal', workspaceKind: 'cer-organizer' },
    editableProps: ['claimStarter', 'evidenceStarter', 'reasoningStarter', 'studentMode'],
    examples: ['Complete the CER organizer to support your claim.'],
  },

  // ── Default: Computation Box ──────────────────────────────────────────────────
  //
  // MUST be the last entry — it has match: {} (catch-all) and score = 0.
  // Every problem that doesn't match a more-specific entry lands here.
  {
    id: 'computation-default',
    specVersion: '3.1.0',
    description: 'Generic computation/work box for math calculations and any problem without a more specific representation.',
    match: {},
    representation: {
      type: 'computation',
    },
    activitySheet: {
      workspaceKind: 'computation',
      wsHeights: WS_HEIGHTS_BY_KIND['computation'],
    },
    assessment: { allowed: true, workspaceKind: 'computation' },
    // Terminal — computation box always renders
    fallback: { kind: 'terminal', workspaceKind: 'computation' },
    editableProps: ['responseBoxHeight'],
    examples: [
      '34 × 6 = ___',
      'Solve: 3x + 5 = 20',
    ],
  },
];
